import React, { useState } from "react";
import { Building2, Plus, Edit2, Trash2, ShieldCheck, DollarSign, Star } from "lucide-react";
import { useApp } from "../../app/context/AppContext";
import { DataTable } from "../../components/common/DataTable";
import { Badge } from "../../components/common/Badge";
import { formatCurrency } from "../../utils/formatters";
import { Modal } from "../../components/common/Modal";
import { useNotification } from "../../app/context/NotificationContext";

export function SupplierModal({ isOpen, onClose, supplierToEdit }) {
  const { addSupplier, updateSupplier } = useApp();
  const { addToast } = useNotification();
  const isEdit = Boolean(supplierToEdit);

  const [formData, setFormData] = useState({
    name: "",
    contactPerson: "",
    phone: "+91 ",
    email: "",
    city: "Mumbai",
    state: "Maharashtra",
    paymentTerms: "Net 21 Days",
    priceCompetitiveness: "Market Standard",
    outstandingBalance: 0
  });

  React.useEffect(() => {
    if (supplierToEdit) {
      setFormData({
        name: supplierToEdit.name || "",
        contactPerson: supplierToEdit.contactPerson || "",
        phone: supplierToEdit.phone || "+91 ",
        email: supplierToEdit.email || "",
        city: supplierToEdit.city || "Mumbai",
        state: supplierToEdit.state || "Maharashtra",
        paymentTerms: supplierToEdit.paymentTerms || "Net 21 Days",
        priceCompetitiveness: supplierToEdit.priceCompetitiveness || "Market Standard",
        outstandingBalance: supplierToEdit.outstandingBalance || 0
      });
    } else {
      setFormData({
        name: "",
        contactPerson: "",
        phone: "+91 ",
        email: "",
        city: "Mumbai",
        state: "Maharashtra",
        paymentTerms: "Net 21 Days",
        priceCompetitiveness: "Market Standard",
        outstandingBalance: 0
      });
    }
  }, [supplierToEdit, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) {
        await updateSupplier(supplierToEdit.id, formData);
        addToast(`Supplier "${formData.name}" updated!`, "success");
      } else {
        await addSupplier(formData);
        addToast(`New supplier "${formData.name}" added!`, "success");
      }
      onClose();
    } catch (err) {
      addToast("Failed to save supplier details.", "error");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit Supplier Record" : "Add FMCG / Agro Supplier"}
      subtitle="Register vendor terms, contact information, and delivery commitments"
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        <div>
          <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
            Supplier Business Name *
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g. Hindustan Unilever Distributor"
            className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Contact Person *
            </label>
            <input
              type="text"
              required
              value={formData.contactPerson}
              onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Phone Number *
            </label>
            <input
              type="text"
              required
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-mono"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Payment Terms *
            </label>
            <select
              value={formData.paymentTerms}
              onChange={(e) => setFormData({ ...formData, paymentTerms: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            >
              <option value="Net 7 Days">Net 7 Days (Mandi APMC)</option>
              <option value="Net 15 Days">Net 15 Days</option>
              <option value="Net 21 Days">Net 21 Days</option>
              <option value="Net 30 Days">Net 30 Days</option>
              <option value="Advance / Immediate">Advance / Immediate</option>
            </select>
          </div>

          <div>
            <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
              City / State
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/30"
          >
            Save Supplier Details
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function SuppliersModule() {
  const { suppliers, deleteSupplier } = useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [supplierToEdit, setSupplierToEdit] = useState(null);

  const columns = [
    {
      header: "Supplier Name",
      accessorKey: "name",
      render: (val, row) => (
        <div>
          <span className="font-bold text-slate-900 dark:text-white">{val}</span>
          <p className="text-[10px] text-slate-400">{row.city}, {row.state}</p>
        </div>
      )
    },
    {
      header: "Contact Details",
      accessorKey: "contactPerson",
      render: (val, row) => (
        <div>
          <p className="text-slate-700 dark:text-slate-300 font-medium">{val}</p>
          <p className="text-[10px] text-slate-400 font-mono">{row.phone}</p>
        </div>
      )
    },
    {
      header: "Terms",
      accessorKey: "paymentTerms",
      render: (val) => <Badge variant="blue">{val}</Badge>
    },
    {
      header: "On-Time Rate",
      accessorKey: "onTimeDeliveryRate",
      align: "center",
      render: (val) => (
        <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
          {val || 95}%
        </span>
      )
    },
    {
      header: "Outstanding Payables",
      accessorKey: "outstandingBalance",
      align: "right",
      render: (val) => (
        <span className="font-bold font-mono text-rose-600 dark:text-rose-400">
          {formatCurrency(val)}
        </span>
      )
    },
    {
      header: "Actions",
      accessorKey: "actions",
      sortable: false,
      render: (_, row) => (
        <div className="flex items-center gap-1.5 justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSupplierToEdit(row);
              setIsModalOpen(true);
            }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Edit Supplier"
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm(`Delete supplier ${row.name}?`)) {
                deleteSupplier(row.id);
              }
            }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
            title="Delete Supplier"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white font-display flex items-center gap-2">
            <Building2 className="w-5 h-5 text-emerald-500" />
            Supplier Master & Vendor Performance Analytics
          </h2>
          <p className="text-xs text-slate-500">
            Vendor master data, on-time delivery rates, price competitiveness, and outstanding payables ledger
          </p>
        </div>

        <button
          onClick={() => {
            setSupplierToEdit(null);
            setIsModalOpen(true);
          }}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl shadow-lg shadow-emerald-600/30 transition"
        >
          <Plus className="w-4 h-4" />
          Add New Supplier
        </button>
      </div>

      <DataTable
        columns={columns}
        data={suppliers}
        title="Approved Supplier Registry"
        subtitle="Manage vendor relationships, APMC mandi accounts, and credit limits"
        filename="kirana_suppliers_master"
      />

      <SupplierModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        supplierToEdit={supplierToEdit}
      />
    </div>
  );
}
